'use es6';

import MessageEditor from './MessageEditor';
import throttle from 'hs-lodash/throttle';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { SEND_TYPING_MESSAGE_THROTTLE_MS } from '../../typing-indicators/constants/typingIndicatorConstants';
import { isPersistedThread } from '../../threads/operators/isPersistedThread';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { MAX_FILE_SIZE } from '../../file-uploads/constants/fileUploadsConstants';
import { List } from 'immutable';
import { buildFileAttachment } from '../../file-uploads/operators/buildFileAttachment';
import { ALLOWED_FILE_TYPES } from 'conversations-internal-schema/file-metadata/constants/allowedFileTypes';
import { debouncedStagingEvent } from '../postInputStagingEvent';
import { jsx as _jsx } from "react/jsx-runtime";
class MessageEditorWrapper extends PureComponent {
  constructor(props) {
    super(props);
    this.publishTypingMessage = throttle(this.props.publishTypingMessage, SEND_TYPING_MESSAGE_THROTTLE_MS, {
      leading: true,
      trailing: false
    });
    this.onChatInputChanged = this.onChatInputChanged.bind(this);
    this.onPublishMessage = this.onPublishMessage.bind(this);
    this.onSelectAttachmentFiles = this.onSelectAttachmentFiles.bind(this);
    this.onRemoveAttachment = this.onRemoveAttachment.bind(this);
    this.inputStagingEvent = debouncedStagingEvent;
  }
  onChatInputChanged(messageText) {
    const {
      currentConversation: thread,
      disablePublish,
      shouldSendInputStagingEvent,
      showInitialMessageBubble,
      setMessageEditorStagingText
    } = this.props;
    setMessageEditorStagingText(messageText);
    if (!disablePublish && messageText !== '') {
      this.publishTypingMessage({
        thread
      });
    }
    if (shouldSendInputStagingEvent) {
      this.inputStagingEvent(messageText, false);
    }
    if (showInitialMessageBubble) {
      this.props.closeInitialMessageBubble();
    }
  }
  onPublishMessage({
    text,
    richText,
    quickReply
  }) {
    const {
      messagesPageUri,
      publishMessage,
      stagedAttachments,
      removeMessageEditorStagingText
    } = this.props;
    this.publishTypingMessage.cancel();
    publishMessage({
      text,
      richText,
      quickReply,
      url: messagesPageUri,
      fileAttachment: buildFileAttachment(stagedAttachments)
    });
    removeMessageEditorStagingText();
  }
  onSelectAttachmentFiles({
    files
  }) {
    if (!files || !files.length) {
      return;
    }
    const {
      onSelectAttachmentFile,
      currentConversation: thread
    } = this.props;
    const file = files.item(0);
    onSelectAttachmentFile({
      file,
      thread
    });
  }
  onRemoveAttachment({
    attachment
  }) {
    const {
      currentConversation,
      onRemoveAttachment
    } = this.props;
    onRemoveAttachment({
      attachment,
      thread: currentConversation
    });
  }
  render() {
    const {
      coloring,
      currentConversation,
      disablePublish,
      disableTextInput,
      isOnFocus,
      mobile,
      networkOffline,
      shouldDisableInitialInputFocus,
      shouldHideAttachmentButton,
      shouldSendInputStagingEvent,
      showingHsBranding,
      stagedAttachments,
      toggleOnAndOffInputFocus,
      stagedMessage,
      showQuickReplyPlaceholder,
      canUploadAttachments
    } = this.props;
    return /*#__PURE__*/_jsx(MessageEditor, {
      coloring: coloring,
      chatInputChanged: this.onChatInputChanged,
      disabled: disableTextInput,
      disablePublish: disablePublish,
      isOnFocus: isOnFocus,
      maxAttachmentFileSize: MAX_FILE_SIZE,
      mobile: mobile,
      networkOffline: networkOffline,
      onSelectAttachmentFiles: this.onSelectAttachmentFiles,
      onRemoveAttachment: this.onRemoveAttachment,
      publishMessage: this.onPublishMessage,
      shouldHideAttachmentButton: shouldHideAttachmentButton,
      shouldDisableInitialInputFocus: shouldDisableInitialInputFocus,
      showingHsBranding: showingHsBranding,
      stagedAttachments: stagedAttachments,
      stagedMessageEditorText: stagedMessage,
      supportsAttachments: isPersistedThread(currentConversation),
      toggleOnAndOffInputFocus: toggleOnAndOffInputFocus,
      fileTypes: ALLOWED_FILE_TYPES,
      showQuickReplyPlaceholder: showQuickReplyPlaceholder,
      shouldSendInputStagingEvent: shouldSendInputStagingEvent,
      inputStaging: this.inputStagingEvent,
      canUploadAttachments: canUploadAttachments
    });
  }
}
MessageEditorWrapper.displayName = 'MessageEditorWrapper';
MessageEditorWrapper.propTypes = {
  canUploadAttachments: PropTypes.bool.isRequired,
  closeInitialMessageBubble: PropTypes.func.isRequired,
  coloring: PropTypes.object.isRequired,
  currentConversation: RecordPropType('Thread'),
  // Conversation dependent
  disablePublish: PropTypes.bool.isRequired,
  disableTextInput: PropTypes.bool.isRequired,
  isOnFocus: PropTypes.bool.isRequired,
  messagesPageUri: PropTypes.string,
  mobile: PropTypes.bool,
  networkOffline: PropTypes.bool.isRequired,
  onRemoveAttachment: PropTypes.func.isRequired,
  // Conversation dependent
  onSelectAttachmentFile: PropTypes.func.isRequired,
  // Conversation dependent
  publishMessage: PropTypes.func.isRequired,
  // Conversation dependent
  publishTypingMessage: PropTypes.func.isRequired,
  // Conversation dependent
  removeMessageEditorStagingText: PropTypes.func.isRequired,
  setMessageEditorStagingText: PropTypes.func.isRequired,
  shouldDisableInitialInputFocus: PropTypes.bool.isRequired,
  shouldHideAttachmentButton: PropTypes.bool.isRequired,
  shouldSendInputStagingEvent: PropTypes.bool.isRequired,
  showInitialMessageBubble: PropTypes.bool.isRequired,
  showQuickReplyPlaceholder: PropTypes.bool.isRequired,
  showingHsBranding: PropTypes.bool.isRequired,
  stagedAttachments: PropTypes.instanceOf(List),
  stagedMessage: PropTypes.string,
  toggleOnAndOffInputFocus: PropTypes.func
};
export default MessageEditorWrapper;